import { useState, useEffect, useCallback } from 'react';
import { gemeenteSlug } from '../utils/mapHelpers';

export default function useAuth() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(true);
  const [showRegister, setShowRegister] = useState(false);

  // JWT helper: decode token en check expiratie (zonder library)
  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  };

  // Probeer access token te vernieuwen met refresh token
  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken || isTokenExpired(refreshToken)) return null;
    try {
      const res = await fetch('/api/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken })
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('jwt_token', data.token);
        localStorage.setItem('refresh_token', data.refresh_token);
        return data.token;
      }
    } catch { /* ignore */ }
    return null;
  };

  // Auto-login on app start if JWT token exists
  useEffect(() => {
    const validateToken = async () => {
      let token = localStorage.getItem('jwt_token');
      if (!token) {
        setIsLoading(false);
        setShowLogin(true);
        return;
      }

      // Client-side expiratie check
      if (isTokenExpired(token)) {
        const newToken = await refreshAccessToken();
        if (!newToken) {
          localStorage.removeItem('jwt_token');
          localStorage.removeItem('refresh_token');
          setIsLoading(false);
          setShowLogin(true);
          return;
        }
        token = newToken;
      }

      try {
        const response = await fetch('/api/me', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setUser({
              username: data.username,
              gemeentenaam: data.gemeentenaam,
              admin: !!data.admin,
              voornaam: data.voornaam || '',
              achternaam: data.achternaam || ''
            });
            setShowLogin(false);
          } else {
            localStorage.removeItem('jwt_token');
            localStorage.removeItem('refresh_token');
            setShowLogin(true);
          }
        } else {
          localStorage.removeItem('jwt_token');
          localStorage.removeItem('refresh_token');
          setShowLogin(true);
        }
      } catch (error) {
        console.error('Token validation error:', error);
        localStorage.removeItem('jwt_token');
        localStorage.removeItem('refresh_token');
        setShowLogin(true);
      } finally {
        setIsLoading(false);
      }
    };

    validateToken();
  }, []);

  // JWT fetch helper met automatische token refresh
  const fetchWithAuth = useCallback(async (url, options = {}) => {
    let token = localStorage.getItem('jwt_token');
    // Client-side check: vernieuw token als het (bijna) verlopen is
    if (isTokenExpired(token)) {
      const newToken = await refreshAccessToken();
      if (!newToken) {
        localStorage.removeItem('jwt_token');
        localStorage.removeItem('refresh_token');
        setUser(null);
        setShowLogin(true);
        setShowRegister(false);
        throw new Error('Sessie verlopen. Log opnieuw in.');
      }
      token = newToken;
    }
    const headers = options.headers ? { ...options.headers } : {};
    if (token) headers['Authorization'] = 'Bearer ' + token;
    const res = await fetch(url, { ...options, headers });
    if (res.status === 401) {
      // Fallback: server zegt 401 ondanks client-side check
      const retryToken = await refreshAccessToken();
      if (retryToken) {
        headers['Authorization'] = 'Bearer ' + retryToken;
        return fetch(url, { ...options, headers });
      }
      localStorage.removeItem('jwt_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
      setShowLogin(true);
      setShowRegister(false);
      throw new Error('Niet geautoriseerd of sessie verlopen. Log opnieuw in.');
    }
    return res;
  }, [setUser, setShowLogin, setShowRegister]);

  const handleLogin = async (data) => {
    // Sla JWT-token en refresh token op in localStorage
    if (data.token) {
      localStorage.setItem('jwt_token', data.token);
    }
    if (data.refresh_token) {
      localStorage.setItem('refresh_token', data.refresh_token);
    }

    // Haal username op via /api/me (die geeft wel username terug)
    let username = data.username;
    let admin = !!data.admin;
    if (!username && data.token) {
      try {
        const meResp = await fetch('/api/me', {
          headers: { 'Authorization': `Bearer ${data.token}` }
        });
        const meData = await meResp.json();
        if (meData.success) {
          username = meData.username;
          admin = !!meData.admin;
        }
      } catch (e) { /* ignore */ }
    }

    setUser({
      username, gemeentenaam: data.gemeentenaam, admin,
      voornaam: data.voornaam || '', achternaam: data.achternaam || '',
    });
    setShowLogin(false);
    setShowRegister(false);

    // In handleLogin, NA succesvolle login:
    const trackLogin = () => {
      // Verwijder eventueel eerder geladen script
      const oldScript = document.querySelector('script[data-website-id]');
      if (oldScript) oldScript.remove();

      // Maak nieuw script aan met username als tag
      const script = document.createElement('script');
      script.defer = true;
      script.src = 'https://analytics.innovom.nl/script.js';
      script.setAttribute('data-website-id', 'c521d768-639c-4c63-9e55-ab31e05f775c');
      script.setAttribute('data-tag', username);
      script.onload = () => {
        if (window.umami) {
          window.umami.track('login');
        }
      };
      document.head.appendChild(script);
    };
    trackLogin();

    // --- URL NIET OVERSCHRIJVEN ALS ER EEN BUURT IN DE URL STAAT ---
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    if (data && data.gemeentenaam && data.gemeentenaam.toLowerCase() !== 'stedin') {
      if (pathParts.length > 0) {
        // Laat URL staan, drilldown wordt door useEffect gedaan
      } else {
        const gemeenteSlugName = gemeenteSlug(data.gemeentenaam);
        if (window.location.pathname !== `/${gemeenteSlugName}`) {
          window.history.replaceState(null, '', `/${gemeenteSlugName}`);
        }
      }
    } else {
      if (window.location.pathname !== '/') {
        window.history.replaceState(null, '', '/');
      }
    }
    // Drilldown wordt nu altijd door useEffect op basis van URL gedaan
  };

  const handleShowRegister = () => {
    setShowRegister(true);
    setShowLogin(false);
  };

  const handleRegisterSuccess = () => {
    setShowRegister(false);
    setShowLogin(true);
  };

  const handleLogout = () => {
    setUser(null);
    setShowLogin(true);
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('refresh_token');
  };

  return {
    user,
    setUser,
    isLoading,
    showLogin,
    showRegister,
    setShowLogin,
    setShowRegister,
    fetchWithAuth,
    handleLogin,
    handleShowRegister,
    handleRegisterSuccess,
    handleLogout
  };
}
